import cn from "classnames";
import BookShow from "components/shared/book/show";
import { Transition } from "components/shared/column";
import Loader from "components/shared/loader";
import { dateTimeInUtcOffset, formatUtcOffset, plainDate, utcTimeInOffset } from "helpers/date";
import { tripSheetFactSourcesEnum } from "helpers/enums/tripSheetFactSources";
import { tripSheetStatusesEnum } from "helpers/enums/tripSheetStatuses";
import { paths } from "helpers/paths";
import { viewContainer } from "helpers/redux";
import React from "react";
import { Route, Switch } from "react-router-dom";
import { config, initialState } from "../formConfig";
import ChangeLog from "./changeLog";
import Markup from "./markup";
import {
    legacyDriverShifts,
    legacyTechnicalInspections,
    normalizeDateTree,
    normalizeTechnicalInspection,
    serializeTree,
} from "./model";

const STATUS_LABELS = {
    [tripSheetStatusesEnum.New]: "Новый",
    [tripSheetStatusesEnum.Issued]: "Выдан",
    [tripSheetStatusesEnum.Closed]: "Закрыт",
    [tripSheetStatusesEnum.Cancelled]: "Отменен",
};

const STATUS_CLASSES = {
    [tripSheetStatusesEnum.New]: "badge-secondary",
    [tripSheetStatusesEnum.Issued]: "badge-primary",
    [tripSheetStatusesEnum.Closed]: "badge-success",
    [tripSheetStatusesEnum.Cancelled]: "badge-danger",
};

const DATE_FIELDS = [
    "validFrom",
    "validTo",
    "documentDate",
    "plannedDepartureAt",
    "actualDepartureAt",
    "plannedReturnAt",
    "actualReturnAt",
    "preTripInspectionAt",
    "medicalExamAt",
];

const ISSUED_EDITABLE_FIELDS = new Set([
    "actualDepartureAt",
    "odometerValueOnDeparture",
    "departureOdometerCorrectionReason",
    "actualReturnAt",
    "odometerValueOnReturn",
    "returnOdometerCorrectionReason",
    "mileageDiscrepancyReason",
    "issuedLiters",
    "issuedCorrectionReason",
    "balanceDepartureLiters",
    "balanceDepartureCorrectionReason",
    "refilledOnRouteLiters",
    "refilledOnRouteCorrectionReason",
    "balanceReturnLiters",
    "balanceReturnCorrectionReason",
    "normChangeCoefficient",
    "mechanicName",
    "dispatcherName",
    "taxerName",
    "controllerName",
    "preTripInspectionAt",
    "medicalWorkerName",
    "medicalExamAt",
    "medicalMarksConfirmedOnPaper",
    "technicalInspectionConfirmedOnPaper",
    "dispatcherComment",
]);

const ODOMETER_SOURCE_FIELDS = {
    odometerValueOnDeparture: "departureOdometerSource",
    odometerValueOnReturn: "returnOdometerSource",
};

const FUEL_SOURCE_FIELDS = {
    issuedLiters: "issuedSource",
    balanceDepartureLiters: "balanceDepartureSource",
    refilledOnRouteLiters: "refilledOnRouteSource",
    balanceReturnLiters: "balanceReturnSource",
};

const TELEMATICS_VALUE_FIELDS = [
    "odometerValueOnDeparture",
    "odometerValueOnReturn",
    "balanceDepartureLiters",
    "balanceReturnLiters",
    "refilledOnRouteLiters",
];

const optionalNumber = (value) => {
    if (value === "" || value === null || value === undefined) {
        return null;
    }
    const number = Number(value);
    return Number.isNaN(number) ? value : number;
};

class TripSheetShow extends BookShow {
    formValuesRequestVersion = 0;
    actionReasonFormRef = React.createRef();
    actionReasonInputRef = React.createRef();

    constructor(props) {
        super(props);
        this.state = {
            ...this.state,
            assignments: [],
            driverShifts: [],
            technicalInspections: [],
            trailerPeriods: [],
            validationResult: null,
            pendingAction: null,
            actionReason: "",
            actionError: "",
        };
    }

    get dropdActions() {
        const actions = [];
        if (this.state.data?.id) {
            actions.push({
                text: "Журнал изменений",
                action: () => this.props.history.push(paths.tripSheetsChangelog(this.state.data.id)),
            });
        }
        return actions;
    }

    getItem = ({ source, match, isNew }) => {
        this.formValuesRequestVersion += 1;
        if (isNew) {
            this.setState({
                data: { ...initialState },
                initial: { ...initialState },
                assignments: [],
                driverShifts: [],
                technicalInspections: [],
                trailerPeriods: [],
                isLoading: false,
                isValid: true,
                shouldUpdate: false,
                validationResult: null,
                pendingAction: null,
                actionReason: "",
                actionError: "",
            });
            return;
        }
        source
            .getItem(match.params)
            .then((data) => {
                if (!data) {
                    this.setState({ isLoading: false });
                    return;
                }
                const {
                    assignments,
                    driverShifts,
                    technicalInspections,
                    trailerPeriods,
                    fuelInfo,
                    responsible,
                    organization,
                    medicalInspectionAt,
                    ...rest
                } = data;
                const flatData = this.normalizeDatesForForm({
                    ...rest,
                    organization,
                    // fallback for responses that return createdAt/medicalInspectionAt instead of form field names
                    documentDate: rest.documentDate ?? rest.createdAt ?? "",
                    medicalExamAt: medicalInspectionAt ?? rest.medicalExamAt ?? "",
                    // flatten fuelInfo
                    issuedLiters: fuelInfo?.issuedLiters ?? "",
                    issuedCorrectionReason: fuelInfo?.issuedCorrectionReason ?? "",
                    balanceDepartureLiters: fuelInfo?.balanceDepartureLiters ?? "",
                    balanceDepartureCorrectionReason: fuelInfo?.balanceDepartureCorrectionReason ?? "",
                    refilledOnRouteLiters: fuelInfo?.refilledOnRouteLiters ?? "",
                    refilledOnRouteCorrectionReason: fuelInfo?.refilledOnRouteCorrectionReason ?? "",
                    balanceReturnLiters: fuelInfo?.balanceReturnLiters ?? "",
                    balanceReturnCorrectionReason: fuelInfo?.balanceReturnCorrectionReason ?? "",
                    normChangeCoefficient: fuelInfo?.normChangeCoefficient ?? "",
                    // flatten responsible
                    mechanicName: responsible?.mechanicName ?? "",
                    dispatcherName: responsible?.dispatcherName ?? "",
                    taxerName: responsible?.taxerName ?? "",
                    controllerName: responsible?.controllerName ?? "",
                    // flatten organization for readonly frontend usage
                    organizationFullName: organization?.fullName ?? "",
                    organizationAddress: organization?.address ?? "",
                    organizationPhoneNumber: organization?.phoneNumber ?? "",
                    organizationOgrn: organization?.ogrn ?? "",
                    organizationOkpo: organization?.okpo ?? "",
                    fuelInfoData: fuelInfo || {},
                    responsibleData: responsible || {},
                });
                const normalizedAssignments = normalizeDateTree(assignments || [], this.dateForForm);
                const normalizedDriverShifts = (
                    driverShifts?.length
                        ? normalizeDateTree(driverShifts, this.dateForForm)
                        : legacyDriverShifts(flatData)
                ).sort((first, second) => first.sequenceNumber - second.sequenceNumber);
                const normalizedTechnicalInspections = technicalInspections?.length
                    ? normalizeDateTree(technicalInspections.map(normalizeTechnicalInspection), this.dateForForm)
                    : legacyTechnicalInspections(flatData);
                this.setState({
                    data: flatData,
                    initial: flatData,
                    assignments: normalizedAssignments,
                    driverShifts: normalizedDriverShifts,
                    technicalInspections: normalizedTechnicalInspections,
                    trailerPeriods: normalizeDateTree(trailerPeriods || [], this.dateForForm),
                    isLoading: false,
                    isValid: true,
                    shouldUpdate: false,
                    validationResult: null,
                    pendingAction: null,
                    actionReason: "",
                    actionError: "",
                });
            })
            .catch(() => this.setState({ isLoading: false }));
    };

    organizationUtcOffset = () => formatUtcOffset(this.props.organizationTimezone);

    getDateFieldProps = (props) => {
        const organizationUtcOffset = this.organizationUtcOffset();
        return {
            ...props,
            wrapper: props.wrapper || "col-auto book-show__date-field",
            errorWrapper: props.errorWrapper || "book-show__date-field-error",
            additionalText: organizationUtcOffset || props.additionalText,
        };
    };

    dateWithOrganizationTimezone = (value) => {
        const organizationUtcOffset = this.organizationUtcOffset();
        if (!value) {
            return value;
        }

        return organizationUtcOffset ? dateTimeInUtcOffset(value, organizationUtcOffset) : value;
    };

    dateForForm = (value) => {
        return plainDate(this.dateWithOrganizationTimezone(value));
    };

    normalizeDatesForForm = (data) => {
        const normalizedData = { ...data };
        DATE_FIELDS.forEach((field) => {
            if (field in normalizedData) {
                normalizedData[field] = this.dateForForm(normalizedData[field]);
            }
        });
        return normalizedData;
    };

    dateForPayload = (value) => {
        if (!value) {
            return null;
        }

        const utcDate = utcTimeInOffset(value, this.organizationUtcOffset() || "+00:00");
        return utcDate === "Invalid date" ? null : utcDate;
    };

    setValue = ({ value, name, option }) => {
        const { data, driverShifts, initial, selectedOptions } = this.state;
        const nextValue = DATE_FIELDS.includes(name) ? plainDate(value) : value;
        const nextData = { ...data, [name]: nextValue };
        let nextDriverShifts = driverShifts;
        if (name === "driverId" && nextValue) {
            if (driverShifts.length === 0) {
                nextDriverShifts = legacyDriverShifts(nextData);
            } else {
                const legacyShiftIndex = driverShifts.findIndex(
                    (shift) => !shift.tripCouplingId && String(shift.driverId) === String(data.driverId),
                );
                if (legacyShiftIndex >= 0) {
                    nextDriverShifts = driverShifts.map((shift, index) =>
                        index === legacyShiftIndex ? { ...shift, driverId: nextValue } : shift,
                    );
                }
            }
        }
        if (name === "driverId" && !nextValue && data.driverId) {
            nextDriverShifts = driverShifts.filter(
                (shift) => shift.tripCouplingId || String(shift.driverId) !== String(data.driverId),
            );
        }
        if (name === "secondaryDriverId" && nextValue && String(data.secondaryDriverId) !== String(nextValue)) {
            const legacyShiftIndex = nextDriverShifts.findIndex(
                (shift) => !shift.tripCouplingId && String(shift.driverId) === String(data.secondaryDriverId),
            );
            const nextDriverAlreadyExists = nextDriverShifts.some(
                (shift, index) =>
                    index !== legacyShiftIndex && String(shift.driverId) === String(nextData.secondaryDriverId),
            );
            if (legacyShiftIndex >= 0) {
                nextDriverShifts = nextDriverAlreadyExists
                    ? nextDriverShifts.filter((_, index) => index !== legacyShiftIndex)
                    : nextDriverShifts.map((shift, index) =>
                          index === legacyShiftIndex ? { ...shift, driverId: nextData.secondaryDriverId } : shift,
                      );
            }
        }
        if (name === "secondaryDriverId" && !nextValue && data.secondaryDriverId) {
            nextDriverShifts = nextDriverShifts.filter(
                (shift) => shift.tripCouplingId || String(shift.driverId) !== String(data.secondaryDriverId),
            );
        }
        if (
            (name === "secondaryDriverId" || (name === "isCrew" && nextValue)) &&
            nextData.secondaryDriverId &&
            !nextDriverShifts.some((shift) => String(shift.driverId) === String(nextData.secondaryDriverId))
        ) {
            nextDriverShifts = [
                ...nextDriverShifts,
                {
                    sequenceNumber: nextDriverShifts.length + 1,
                    driverId: nextData.secondaryDriverId,
                    isCrew: !!nextData.isCrew,
                    medicalInspections: [],
                },
            ];
        }
        if (name === "isCrew" && nextDriverShifts.length > 0) {
            nextDriverShifts = nextDriverShifts.map((shift) => ({ ...shift, isCrew: !!nextValue }));
        }
        const odometerSourceField = ODOMETER_SOURCE_FIELDS[name];
        if (odometerSourceField && data[name] !== nextValue) {
            nextData[odometerSourceField] = [
                tripSheetFactSourcesEnum.Telematics,
                tripSheetFactSourcesEnum.Corrected,
            ].includes(data[odometerSourceField])
                ? tripSheetFactSourcesEnum.Corrected
                : tripSheetFactSourcesEnum.Manual;
        }
        const fuelSourceField = FUEL_SOURCE_FIELDS[name];
        if (fuelSourceField && data[name] !== nextValue) {
            const fuelInfoData = data.fuelInfoData || {};
            nextData.fuelInfoData = {
                ...fuelInfoData,
                [fuelSourceField]: [
                    tripSheetFactSourcesEnum.Telematics,
                    tripSheetFactSourcesEnum.Corrected,
                    tripSheetFactSourcesEnum.Bid,
                    tripSheetFactSourcesEnum.ExternalSystem,
                ].includes(fuelInfoData[fuelSourceField])
                    ? tripSheetFactSourcesEnum.Corrected
                    : tripSheetFactSourcesEnum.Manual,
            };
        }
        let shouldUpdate = nextDriverShifts !== driverShifts;
        for (const key in data) {
            if (initial[key] !== nextData[key]) {
                shouldUpdate = true;
                break;
            }
        }
        const isValid = this.isValidForm(nextData);
        this.setState({
            data: nextData,
            driverShifts: nextDriverShifts,
            shouldUpdate,
            selectedOptions: { ...selectedOptions, [name]: option },
            ...(isValid ? { isValid: true } : {}),
        });

        // Автозаполнение одометра и топлива
        const autoFillTriggers = ["carId", "actualDepartureAt", "actualReturnAt"];
        if (TELEMATICS_VALUE_FIELDS.includes(name) || autoFillTriggers.includes(name)) {
            this.formValuesRequestVersion += 1;
        }
        if (autoFillTriggers.includes(name)) {
            const { carId, actualDepartureAt, actualReturnAt } = nextData;
            if (carId && actualDepartureAt && actualReturnAt) {
                const requestVersion = this.formValuesRequestVersion;
                this.props.source
                    .getFormValues({
                        carId,
                        from: this.dateWithOrganizationTimezone(actualDepartureAt),
                        to: this.dateWithOrganizationTimezone(actualReturnAt),
                    })
                    .then((values) => {
                        if (values && requestVersion === this.formValuesRequestVersion) {
                            this.setState((prev) => ({
                                data: {
                                    ...prev.data,
                                    odometerValueOnDeparture:
                                        values.odometerValueOnDeparture ?? prev.data.odometerValueOnDeparture,
                                    odometerValueOnReturn:
                                        values.odometerValueOnReturn ?? prev.data.odometerValueOnReturn,
                                    departureOdometerSource:
                                        values.odometerValueOnDeparture !== null &&
                                        values.odometerValueOnDeparture !== undefined
                                            ? values.departureOdometerSource || tripSheetFactSourcesEnum.Telematics
                                            : prev.data.departureOdometerSource,
                                    returnOdometerSource:
                                        values.odometerValueOnReturn !== null &&
                                        values.odometerValueOnReturn !== undefined
                                            ? values.returnOdometerSource || tripSheetFactSourcesEnum.Telematics
                                            : prev.data.returnOdometerSource,
                                    departureOdometerFixedAt:
                                        values.departureOdometerFixedAt ?? prev.data.departureOdometerFixedAt,
                                    returnOdometerFixedAt:
                                        values.returnOdometerFixedAt ?? prev.data.returnOdometerFixedAt,
                                    balanceDepartureLiters:
                                        values.fuelInfo?.balanceDepartureLiters ?? prev.data.balanceDepartureLiters,
                                    balanceReturnLiters:
                                        values.fuelInfo?.balanceReturnLiters ?? prev.data.balanceReturnLiters,
                                    refilledOnRouteLiters:
                                        values.fuelInfo?.refilledOnRouteLiters ?? prev.data.refilledOnRouteLiters,
                                    fuelInfoData: {
                                        ...prev.data.fuelInfoData,
                                        ...(values.fuelInfo?.balanceDepartureLiters !== null &&
                                        values.fuelInfo?.balanceDepartureLiters !== undefined
                                            ? {
                                                  balanceDepartureSource:
                                                      values.fuelInfo.balanceDepartureSource ||
                                                      tripSheetFactSourcesEnum.Telematics,
                                              }
                                            : {}),
                                        ...(values.fuelInfo?.balanceReturnLiters !== null &&
                                        values.fuelInfo?.balanceReturnLiters !== undefined
                                            ? {
                                                  balanceReturnSource:
                                                      values.fuelInfo.balanceReturnSource ||
                                                      tripSheetFactSourcesEnum.Telematics,
                                              }
                                            : {}),
                                        ...(values.fuelInfo?.refilledOnRouteLiters !== null &&
                                        values.fuelInfo?.refilledOnRouteLiters !== undefined
                                            ? {
                                                  refilledOnRouteSource:
                                                      values.fuelInfo.refilledOnRouteSource ||
                                                      tripSheetFactSourcesEnum.Telematics,
                                              }
                                            : {}),
                                    },
                                },
                                shouldUpdate: true,
                            }));
                        }
                    })
                    .catch(() => undefined);
            }
        }
    };

    setAssignments = (assignments) => {
        this.setState({ assignments, shouldUpdate: true });
    };

    setCollection = (name, value) => {
        this.setState({ [name]: value, shouldUpdate: true });
    };

    apply = () => {
        const { source, match, isNew, history, mergeParams } = this.props;
        const { isLoading, shouldUpdate, data, assignments, driverShifts, technicalInspections, trailerPeriods } =
            this.state;

        if (!isLoading && shouldUpdate) {
            if (this.isValidForm(data)) {
                this.setState({ isLoading: true, isValid: true });
                const payload = this.buildPayload(
                    data,
                    assignments,
                    driverShifts,
                    technicalInspections,
                    trailerPeriods,
                    match,
                    isNew,
                );
                source
                    .apply(payload)
                    .then((res) => {
                        if (res) {
                            if (isNew && res.id) {
                                history.push(paths.tripSheetsShow(res.id));
                            } else {
                                this.update(mergeParams);
                            }
                        } else {
                            this.setState({ isLoading: false, isValid: false });
                        }
                    })
                    .catch(() => this.setState({ isLoading: false, isValid: false }));
            } else {
                this.setState({ isValid: false });
            }
        }
    };

    buildPayload = (data, assignments, driverShifts, technicalInspections, trailerPeriods, match, isNew) => {
        const {
            issuedLiters,
            issuedCorrectionReason,
            balanceDepartureLiters,
            balanceDepartureCorrectionReason,
            refilledOnRouteLiters,
            refilledOnRouteCorrectionReason,
            balanceReturnLiters,
            balanceReturnCorrectionReason,
            normChangeCoefficient,
            mechanicName,
            dispatcherName,
            taxerName,
            controllerName,
            organization,
            organizationFullName,
            organizationAddress,
            organizationPhoneNumber,
            organizationOgrn,
            organizationOkpo,
            fuelInfoData,
            responsibleData,
            medicalExamAt,
            ...rest
        } = data;

        const restWithOrganizationDates = { ...rest };
        DATE_FIELDS.filter((field) => field !== "medicalExamAt").forEach((field) => {
            if (field in restWithOrganizationDates) {
                restWithOrganizationDates[field] = this.dateForPayload(restWithOrganizationDates[field]);
            }
        });

        const payload = {
            ...restWithOrganizationDates,
            odometerValueOnDeparture: optionalNumber(restWithOrganizationDates.odometerValueOnDeparture),
            odometerValueOnReturn: optionalNumber(restWithOrganizationDates.odometerValueOnReturn),
            medicalInspectionAt: this.dateForPayload(medicalExamAt),
            fuelInfo: {
                ...fuelInfoData,
                issuedLiters: optionalNumber(issuedLiters),
                issuedCorrectionReason: issuedCorrectionReason || null,
                balanceDepartureLiters: optionalNumber(balanceDepartureLiters),
                balanceDepartureCorrectionReason: balanceDepartureCorrectionReason || null,
                refilledOnRouteLiters: optionalNumber(refilledOnRouteLiters),
                refilledOnRouteCorrectionReason: refilledOnRouteCorrectionReason || null,
                balanceReturnLiters: optionalNumber(balanceReturnLiters),
                balanceReturnCorrectionReason: balanceReturnCorrectionReason || null,
                normChangeCoefficient: optionalNumber(normChangeCoefficient),
            },
            responsible: {
                ...responsibleData,
                mechanicName: mechanicName || null,
                dispatcherName: dispatcherName || null,
                taxerName: taxerName || null,
                controllerName: controllerName || null,
            },
            assignments: serializeTree(assignments, this.dateForPayload),
            driverShifts: serializeTree(driverShifts, this.dateForPayload),
            technicalInspections: serializeTree(
                technicalInspections.map(normalizeTechnicalInspection),
                this.dateForPayload,
            ),
            trailerPeriods: serializeTree(trailerPeriods, this.dateForPayload),
        };

        if (!isNew) {
            payload.id = Number(match.params.id);
        }

        // Убираем пустые строки из payload верхнего уровня
        Object.keys(payload).forEach((key) => {
            if (payload[key] === "") {
                payload[key] = null;
            }
        });

        return payload;
    };

    runStatusChange = (newStatus, reason = null) => {
        const { source, mergeParams } = this.props;
        const { id } = this.state.data;
        const { isLoading } = this.state;
        if (!id || isLoading) {
            return;
        }

        this.setState({ isLoading: true, pendingAction: null, validationResult: null, actionError: "" });
        source
            .setStatus({ id, status: newStatus, reason: reason || null })
            .then((res) => {
                if (res) {
                    this.update(mergeParams);
                } else {
                    this.setState({ isLoading: false });
                }
            })
            .catch(() => this.setState({ isLoading: false }));
    };

    prepareStatusChange = (newStatus) => {
        const { id } = this.state.data;
        if (!id || this.state.isLoading) {
            return;
        }
        if (this.state.shouldUpdate) {
            this.setState({
                validationResult: {
                    errors: ["Сохраните изменения перед переходом в другой статус"],
                    warnings: [],
                    isValid: false,
                },
                pendingAction: null,
            });
            return;
        }

        if ([tripSheetStatusesEnum.Issued, tripSheetStatusesEnum.Closed].includes(newStatus)) {
            this.setState({ isLoading: true, validationResult: null, pendingAction: null, actionReason: "" });
            this.props.source
                .validate({ id, targetStatus: newStatus })
                .then((validationResult) => {
                    const result = validationResult || { errors: [], warnings: [], isValid: false };
                    const errors = [...(result.errors || [])];
                    const warnings = result.warnings || [];
                    if (result.isValid === false && errors.length === 0 && warnings.length === 0) {
                        errors.push("Сервер не подтвердил готовность путевого листа к переходу");
                    }
                    const normalizedResult = { ...result, errors, warnings };
                    if (errors.length > 0) {
                        this.setState({ isLoading: false, validationResult: normalizedResult });
                        return;
                    }
                    if (warnings.length > 0) {
                        this.setState({
                            isLoading: false,
                            validationResult: normalizedResult,
                            pendingAction: { kind: "status", status: newStatus, reasonRequired: true },
                        });
                        return;
                    }

                    this.setState({ isLoading: false }, () => this.runStatusChange(newStatus));
                })
                .catch(() =>
                    this.setState({
                        isLoading: false,
                        validationResult: {
                            errors: ["Не удалось выполнить проверку путевого листа. Повторите попытку."],
                            warnings: [],
                            isValid: false,
                        },
                    }),
                );
            return;
        }

        this.setState(
            {
                pendingAction: { kind: "status", status: newStatus, reasonRequired: true },
                validationResult: null,
                actionReason: "",
                actionError: "",
            },
            this.scrollToActionReason,
        );
    };

    /** Focus the mounted reason field and reveal its form after cancellation or restoration is requested. */
    scrollToActionReason = () => {
        const form = this.actionReasonFormRef.current;
        const input = this.actionReasonInputRef.current;
        if (!form || !input) {
            return;
        }
        input.focus({ preventScroll: true });
        form.scrollIntoView({
            block: "start",
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        });
    };

    prepareCorrection = () => {
        this.setState({
            pendingAction: { kind: "correction", reasonRequired: true },
            validationResult: null,
            actionReason: "",
            actionError: "",
        });
    };

    confirmPendingAction = () => {
        const { pendingAction, actionReason, data, isLoading } = this.state;
        if (!pendingAction || isLoading) {
            return;
        }
        const reason = actionReason.trim();
        if (pendingAction.reasonRequired && !reason) {
            this.setState({ actionError: "Укажите осмысленную причину действия" });
            return;
        }

        if (pendingAction.kind === "correction") {
            this.setState({ isLoading: true, actionError: "" });
            this.props.source
                .createCorrection({ id: data.id, reason })
                .then((result) => {
                    if (result?.id) {
                        this.props.history.push(paths.tripSheetsShow(result.id));
                    } else {
                        this.setState({ isLoading: false });
                    }
                })
                .catch(() => this.setState({ isLoading: false }));
            return;
        }

        this.runStatusChange(pendingAction.status, reason);
    };

    cancelPendingAction = () => {
        this.setState({ pendingAction: null, validationResult: null, actionReason: "", actionError: "" });
    };

    printForm = () => {
        const { source } = this.props;
        const { id } = this.state.data;
        if (!id) {
            return;
        }
        const pdfWindow = window.open("", "_blank");
        if (pdfWindow) {
            pdfWindow.opener = null;
        }
        source
            .generatePrintForm({ id })
            .then((res) => {
                if (res?.accessUrlLink) {
                    this.openPdfInNewTab(res.accessUrlLink, pdfWindow);
                } else {
                    pdfWindow?.close();
                }
            })
            .catch(() => {
                pdfWindow?.close();
            });
    };

    openPdfInNewTab = (accessUrlLink, pdfWindow) => {
        fetch(accessUrlLink)
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Failed to load PDF");
                }
                return response.blob();
            })
            .then((blob) => {
                const pdfBlob = new Blob([blob], { type: "application/pdf" });
                const pdfUrl = window.URL.createObjectURL(pdfBlob);
                let isRevoked = false;
                const revokePdfUrl = () => {
                    if (isRevoked) {
                        return;
                    }

                    isRevoked = true;
                    window.URL.revokeObjectURL(pdfUrl);
                };
                const revokeTimeout = window.setTimeout(revokePdfUrl, 60000);
                const attachCleanup = (targetWindow) => {
                    if (!targetWindow) {
                        return;
                    }

                    targetWindow.addEventListener(
                        "load",
                        () => {
                            window.clearTimeout(revokeTimeout);
                            revokePdfUrl();
                        },
                        { once: true },
                    );
                };

                if (pdfWindow && !pdfWindow.closed) {
                    attachCleanup(pdfWindow);
                    pdfWindow.location.href = pdfUrl;
                    return;
                }

                const newPdfWindow = window.open(pdfUrl, "_blank", "noopener");
                attachCleanup(newPdfWindow);
            })
            .catch(() => {
                if (pdfWindow && !pdfWindow.closed) {
                    pdfWindow.location.href = accessUrlLink;
                    return;
                }

                window.open(accessUrlLink, "_blank", "noopener");
            });
    };

    renderStatusBadge = () => {
        const { status } = this.state.data;
        if (!status) {
            return null;
        }
        const badgeClass = STATUS_CLASSES[status] || "badge-secondary";
        return <span className={cn("badge align-middle ml-2", badgeClass)}>{STATUS_LABELS[status] || status}</span>;
    };

    renderStatusActions = () => {
        const { canEditBooks } = this.props;
        const { status, id } = this.state.data;
        if (!id) {
            return null;
        }

        return (
            <div className="d-inline-flex align-items-center flex-wrap mr-2">
                {canEditBooks && status === tripSheetStatusesEnum.New && (
                    <button
                        type="button"
                        className="btn btn-sm btn-brand mr-1"
                        onClick={() => this.prepareStatusChange(tripSheetStatusesEnum.Issued)}
                    >
                        Выдать
                    </button>
                )}
                {canEditBooks && status === tripSheetStatusesEnum.Issued && (
                    <button
                        type="button"
                        className="btn btn-sm btn-secondary mr-1"
                        onClick={() => this.prepareStatusChange(tripSheetStatusesEnum.Closed)}
                    >
                        Закрыть
                    </button>
                )}
                <button type="button" className="btn btn-sm btn-primary mr-1" onClick={this.printForm}>
                    Печать
                </button>
                {canEditBooks && [tripSheetStatusesEnum.New, tripSheetStatusesEnum.Issued].includes(status) && (
                    <button
                        type="button"
                        className="btn btn-sm btn-danger mr-1"
                        onClick={() => this.prepareStatusChange(tripSheetStatusesEnum.Cancelled)}
                    >
                        Отменить
                    </button>
                )}
                {canEditBooks && status === tripSheetStatusesEnum.Cancelled && (
                    <button
                        type="button"
                        className="btn btn-sm btn-secondary mr-1"
                        onClick={() => this.prepareStatusChange(tripSheetStatusesEnum.New)}
                    >
                        Восстановить
                    </button>
                )}
                {canEditBooks && status === tripSheetStatusesEnum.Closed && (
                    <button type="button" className="btn btn-sm btn-secondary mr-1" onClick={this.prepareCorrection}>
                        Создать корректировку
                    </button>
                )}
            </div>
        );
    };

    renderHead = () => {
        const { back, title, isNew } = this.props;
        const { data } = this.state;
        const displayTitle = isNew ? "Новый путевой лист" : `Путевой лист ${data.number || ""}`;
        return (
            <div className="book-show__head">
                <button
                    type="button"
                    className="book-show__close close close--sm"
                    aria-label="Закрыть карточку"
                    onClick={back}
                />
                <div className="book-show__title">
                    {displayTitle || title}
                    {!isNew && this.renderStatusBadge()}
                </div>
                {!isNew && (
                    <div className="d-flex align-items-center flex-wrap ml-auto">
                        {this.renderStatusActions()}
                        {!!this.dropdActions.length && (
                            <div className="book-show__dropdown c-dropd">
                                <button
                                    type="button"
                                    className="c-dropd__btn"
                                    aria-label="Другие действия"
                                    onClick={this.openDropd}
                                />
                                {this.renderDropd()}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    isFieldEditable = (name) => {
        if (!this.props.canEditBooks) {
            return false;
        }
        if (this.props.isNew || !this.state.data.status || this.state.data.status === tripSheetStatusesEnum.New) {
            return true;
        }
        return this.state.data.status === tripSheetStatusesEnum.Issued && ISSUED_EDITABLE_FIELDS.has(name);
    };

    renderWorkflowFeedback = () => {
        const { validationResult, pendingAction, actionReason, actionError } = this.state;
        if (!validationResult && !pendingAction) {
            return null;
        }

        const errors = validationResult?.errors || [];
        const warnings = validationResult?.warnings || [];
        const actionLabel =
            pendingAction?.kind === "correction"
                ? "Создание корректировки"
                : `Переход в статус «${STATUS_LABELS[pendingAction?.status] || ""}»`;

        return (
            <div className="mx-3 mt-3">
                {errors.length > 0 && (
                    <div className="alert alert-danger mb-2">
                        <div className="font-weight-bold mb-1">Действие заблокировано</div>
                        <ul className="mb-0 pl-4">
                            {errors.map((error, index) => (
                                <li key={`${error}_${index}`}>{error}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {warnings.length > 0 && (
                    <div className="alert alert-warning mb-2">
                        <div className="font-weight-bold mb-1">Требуется подтверждение предупреждений</div>
                        <ul className="mb-0 pl-4">
                            {warnings.map((warning, index) => (
                                <li key={`${warning}_${index}`}>{warning}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {pendingAction && (
                    <div className="border rounded p-3 mb-3" ref={this.actionReasonFormRef}>
                        <div className="font-weight-bold mb-2">{actionLabel}</div>
                        <label className="small text-muted" htmlFor="trip-sheet-action-reason">
                            Причина
                        </label>
                        <textarea
                            id="trip-sheet-action-reason"
                            ref={this.actionReasonInputRef}
                            className={cn("form-control form-control-sm", { "is-invalid": actionError })}
                            rows={2}
                            value={actionReason}
                            onChange={(event) => this.setState({ actionReason: event.target.value, actionError: "" })}
                        />
                        {actionError && <div className="invalid-feedback d-block">{actionError}</div>}
                        <div className="mt-2">
                            <button
                                type="button"
                                className="btn btn-brand btn-sm mr-2"
                                onClick={this.confirmPendingAction}
                            >
                                Подтвердить
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={this.cancelPendingAction}
                            >
                                Отмена
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    renderButton = () => {
        const status = this.state.data.status;
        if (
            !this.props.canEditBooks ||
            (!this.props.isNew && ![tripSheetStatusesEnum.New, tripSheetStatusesEnum.Issued].includes(status))
        ) {
            return null;
        }
        const cname = cn("btn btn-brand btn-sm", { disabled: !this.state.shouldUpdate });
        return (
            <div className="panel__row panel__row--button mb-3">
                <button type="button" className={cname} disabled={!this.state.shouldUpdate} onClick={this.apply}>
                    {this.props.isNew ? "Создать" : "Обновить"}
                </button>
            </div>
        );
    };

    renderBodyWrap = () => {
        const { isLoading } = this.state;

        if (isLoading) {
            return <Loader prefix="absolute-center" />;
        }

        return (
            <div className="overflow-auto h-100 native-scroll__wrap">
                <div className="native-scroll__container">
                    {this.renderWorkflowFeedback()}
                    {this.renderDocumentTab()}
                </div>
            </div>
        );
    };

    renderChangeLogHead = () => {
        const { history, match } = this.props;
        const tripSheetId = this.state.data.id || match.params.id;
        return (
            <div className="book-show__head">
                <button
                    type="button"
                    className="book-show__close close close--sm"
                    aria-label="Вернуться к путевому листу"
                    onClick={() => history.push(paths.tripSheetsShow(tripSheetId))}
                />

                <div className="book-show__title">Журнал изменений</div>
            </div>
        );
    };

    renderDocumentTab = () => {
        const { config: cfgProp, canEditBooks } = this.props;
        const { assignments, driverShifts, technicalInspections, trailerPeriods } = this.state;
        const status = this.state.data.status;
        const canEditPlan = canEditBooks && (this.props.isNew || !status || status === tripSheetStatusesEnum.New);
        const canEditFacts =
            canEditBooks &&
            (this.props.isNew || !status || [tripSheetStatusesEnum.New, tripSheetStatusesEnum.Issued].includes(status));
        return (
            <Markup
                {...cfgProp}
                renderField={this.renderField}
                renderSelect={this.renderSelect}
                renderDateField={this.renderDateField}
                renderCheckbox={this.renderCheckbox}
                data={this.state.data}
                assignments={assignments}
                onAssignmentsChange={this.setAssignments}
                driverShifts={driverShifts}
                onDriverShiftsChange={(value) => this.setCollection("driverShifts", value)}
                technicalInspections={technicalInspections}
                onTechnicalInspectionsChange={(value) => this.setCollection("technicalInspections", value)}
                trailerPeriods={trailerPeriods}
                onTrailerPeriodsChange={(value) => this.setCollection("trailerPeriods", value)}
                canEditPlan={canEditPlan}
                canEditFacts={canEditFacts}
                status={status}
                organizationTimezone={this.props.organizationTimezone}
                source={this.props.source}
                reload={() => this.update(this.props.mergeParams)}
                onChangeReasonRequired={(message) => {
                    this.setState({
                        validationResult: { errors: [], warnings: [message], isValid: true },
                    });
                }}
                Button={this.renderButton}
            />
        );
    };

    render() {
        const { match, location, history, isNew, source, modal } = this.props;
        const { isValid, data } = this.state;
        const cname = cn("book-show__body", { "book-show__body--invalid": !isValid });
        const documentRoute = isNew ? paths.tripSheetsNew : paths.tripSheetsShow(":id");

        return (
            <Switch>
                {!isNew && (
                    <Route
                        path={paths.tripSheetsChangelog(":id")}
                        render={(routeProps) => (
                            <Transition {...routeProps} dir="full" wrapper="page-col--overflow-inherit">
                                <div className="page-book__show book-show">
                                    {this.renderChangeLogHead()}
                                    <div className="book-show__body">
                                        <ChangeLog
                                            tripSheetId={data.id || routeProps.match.params.id}
                                            source={source}
                                            modal={modal}
                                            organizationTimezone={this.props.organizationTimezone}
                                        />
                                    </div>
                                </div>
                            </Transition>
                        )}
                    />
                )}
                <Route
                    exact
                    path={documentRoute}
                    render={() => (
                        <Transition dir="full" wrapper="page-col--overflow-inherit" {...{ match, location, history }}>
                            <div className="page-book__show book-show">
                                {this.renderHead()}
                                <div className={cname}>{this.renderBodyWrap()}</div>
                            </div>
                        </Transition>
                    )}
                />
            </Switch>
        );
    }
}

const ConfiguredShow = (props) => <TripSheetShow {...props} config={config} initialState={initialState} />;

export default viewContainer(ConfiguredShow, "trip-sheets");
